import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { LinkedOaStore } from '@app/pages/chat/linked-oa.store';
import { LinkedOAInfo } from '@app/pages/chat/types/chat';

import { isDevice } from '../../../../utils/utils';
import { AvatarComponent } from '../../../avatar/avatar.component';
import { AppIconComponent } from '../../../icon/icon.component';
import { TammiDeviceBadgeComponent } from '../../../tammi-device-badge/tammi-device-badge.component';
import { CALL_END_REASON, CALL_STATE, CALL_TYPE } from '../../constants';
import { CallStoreService } from '../../services/call-store.service';

@Component({
  selector: 'app-call-audio-layout',
  standalone: true,
  imports: [AvatarComponent, TammiDeviceBadgeComponent, AppIconComponent],
  templateUrl: './call-audio-layout.component.html',
  styleUrl: './call-audio-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallAudioLayoutComponent {
  isFullscreen = input<boolean>(false);

  callStore = inject(CallStoreService);
  private linkedOaStore = inject(LinkedOaStore);

  CALL_STATE = CALL_STATE;
  CALL_TYPE = CALL_TYPE;

  readonly callState = this.callStore.callState;
  readonly currentCall = this.callStore.currentCall;
  readonly callType = this.callStore.callType;
  readonly hasCurrentCall = this.callStore.hasCurrentCall;
  readonly callEndReason = this.callStore.callEndReason;
  readonly participants = this.callStore.participants;
  readonly isShowMyNetworkReconnecting = this.callStore.isShowMyNetworkReconnecting;
  readonly isShowRemoteNetworkWarning = this.callStore.isShowRemoteNetworkWarning;
  readonly isShowMyNetworkWarning = this.callStore.isShowMyNetworkWarning;
  readonly remoteSoundLevels = this.callStore.remoteSoundLevels;

  participant = computed(() => (this.participants().length > 0 ? this.participants()[0] : null));
  isDevice = computed(() => isDevice(this.participant()?.userId ?? ''));

  readonly callDuration = this.callStore.callDurationText;

  readonly networkQualityInfoName = this.callStore.networkQualityInfoName;

  readonly internetSource = computed(
    () => this.currentCall()?.extendedData?.source?.toLocaleLowerCase() === 'internet',
  );
  readonly address = computed(() => this.callStore.currentCall()?.extendedData?.address);

  readonly showSpeakingRing = computed(() => {
    const participant = this.participant();
    if (!participant) return this.callState() === CALL_STATE.RINGING;
    const level = this.remoteSoundLevels()[participant.userId] ?? 0;
    return this.callState() === CALL_STATE.RINGING || level > 10;
  });

  readonly linkedOaInfo = computed<LinkedOAInfo | null>(() => {
    const oaUserId = this.currentCall()?.extendedData?.oaUserId;
    const calleeId = this.participant()?.userId;

    const userId = this.callStore.isInviter() ? calleeId : oaUserId;

    if (!userId) return null;
    return this.linkedOaStore.getLinkedOa(userId) ?? null;
  });

  readonly getCallStateText = computed(() => {
    const call = this.currentCall();
    const state = this.callState();
    const isInviter = call?.inviter === this.callStore.zimService.userId;
    switch (state) {
      case CALL_STATE.IDLE:
        return 'Sẵn sàng';
      case CALL_STATE.OUTGOING:
        return isInviter ? 'Đang gọi' : 'Đang kết nối';
      case CALL_STATE.RINGING:
        return isInviter ? 'Đang đổ chuông' : 'Đang kết nối';
      case CALL_STATE.CONNECTING:
        return 'Đang nối máy đến người nhận';
      case CALL_STATE.CONNECTED:
        return 'Đã kết nối';
      case CALL_STATE.ENDED:
        return 'Kết thúc';
      default:
        return 'Không xác định';
    }
  });

  readonly getEndReasonText = computed(() => {
    const reason = this.callEndReason();
    switch (reason) {
      case CALL_END_REASON.CANCELLED:
        return this.callStore.isInviter() ? 'Bạn đã hủy cuộc gọi' : 'Cuộc gọi đã bị hủy';
      case CALL_END_REASON.TIMEOUT:
        return this.callStore.isInviter()
          ? 'Người nhận không liên lạc được'
          : 'Cuộc gọi đã hết thời gian';
      case CALL_END_REASON.NO_ANSWER:
        return this.callStore.isInviter() ? 'Người nhận không bắt máy' : 'Cuộc gọi nhỡ';
      case CALL_END_REASON.REJECTED:
        return this.callStore.isInviter()
          ? 'Người nhận đã từ chối cuộc gọi'
          : 'Bạn đã từ chối cuộc gọi';
      case CALL_END_REASON.NETWORK:
        return 'Cuộc gọi đã kết thúc do kết nối không ổn định';
      case CALL_END_REASON.BUSY:
        return 'Người nhận đang bận';
      case CALL_END_REASON.ENDED:
      case CALL_END_REASON.NORMAL:
        return 'Cuộc gọi kết thúc';
      default:
        return 'Cuộc gọi kết thúc';
    }
  });
}
